import React from "react";
import styled from "styled-components";
import produce from "immer";
import { omit } from "lodash";
import { Checklist } from "@sonder/ui/source/styled/components/Checklist";
import {
  ChecklistFooter,
  ChecklistItem,
  ChecklistItemBaseProps,
} from "@sonder/ui/source/styled/components/ChecklistItem";
import {
  createTaskChecklistItemOptimisticResponse,
  useCreateTaskChecklistItem,
} from "core/queries/CreateTaskChecklistItem";
import {
  GetTaskResponse,
  TaskChecklistItem,
  TaskData,
  getTask,
} from "core/queries/GetTask";
import {
  updateTaskChecklistItemOptimisticResponse,
  useUpdateTaskChecklistItem,
} from "core/queries/UpdateTaskChecklistItem";
import { ChecklistItemStatus } from "typings/globalGQLTypes";

interface TaskChecklistProps {
  task: TaskData;
}

type TaskChecklistItemProps = TaskChecklistItem | ChecklistItemBaseProps;

const OPTIMISTIC_PREFIX = "OptimisticTaskChecklistItem";

const StyledChecklistTitle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledRequired = styled.span`
  margin-right: 2.7rem;
`;

export const TaskChecklist = ({ task }: TaskChecklistProps) => {
  const checkList = task.checklistItems || [];

  const [onCreateTaskChecklistItem, { loading: isAdding }] =
    useCreateTaskChecklistItem({
      refetchQueries: ["GetTask"],
      awaitRefetchQueries: true,
    });

  const [onUpdateTaskChecklistItem] = useUpdateTaskChecklistItem({
    refetchQueries: ["GetTask"],
    awaitRefetchQueries: true,
  });

  const onAdd = ({ name, required }: TaskChecklistItemProps) => {
    const taskId = task.id;
    const optimisticId = `${OPTIMISTIC_PREFIX}|-${new Date().getTime()}`;
    if (name) {
      onCreateTaskChecklistItem({
        variables: {
          taskId,
          name,
          required,
        },
        optimisticResponse: createTaskChecklistItemOptimisticResponse({
          id: optimisticId,
          createdAt: new Date().toISOString(),
          name,
          required: !!required,
          status: ChecklistItemStatus.active,
        }),
        update: (proxy, { data }) => {
          if (!data || !data.createTaskChecklistItem) {
            return;
          }
          const {
            createTaskChecklistItem: { checklistItem: newChecklistItem },
          } = data;

          const variables = { taskId: taskId.split("|")[1] };
          const newData = proxy.readQuery<GetTaskResponse>({
            query: getTask,
            variables,
          });
          if (!newData) return;

          proxy.writeQuery({
            data: produce(newData, draft => {
              if (draft.task && draft.task.checklistItems) {
                draft.task.checklistItems.push(
                  newChecklistItem as TaskChecklistItem,
                );
              }
              return draft;
            }),
            query: getTask,
            variables,
          });
        },
      });
    }
  };

  const onChange = (newChecklistItem: TaskChecklistItemProps) => {
    const oldChecklistItem = checkList.find(c => c.id === newChecklistItem.id);
    if (!oldChecklistItem) {
      return;
    }

    const updatedChecklistItem = {
      ...oldChecklistItem,
      ...newChecklistItem,
    };
    onUpdateTaskChecklistItem({
      variables: {
        ...omit(updatedChecklistItem, ["id"]),
        checklistItemId: updatedChecklistItem.id,
      },
      optimisticResponse:
        updateTaskChecklistItemOptimisticResponse(updatedChecklistItem),
    });
  };

  const onToggle = ({ id, isChecked }: ChecklistItemBaseProps) =>
    onChange({
      id,
      status: isChecked
        ? ChecklistItemStatus.active
        : ChecklistItemStatus.completed,
    });

  const onDelete = ({ id }: TaskChecklistItemProps) =>
    onChange({
      id,
      status: ChecklistItemStatus.deleted,
    });

  const renderChecklistItems = checkList
    .filter(c => c.status !== ChecklistItemStatus.deleted)
    .map(c => {
      const isChecked = c.status === ChecklistItemStatus.completed;
      return (
        <ChecklistItem
          id={c.id}
          isChecked={isChecked}
          key={c.id}
          required={!!c.required}
          onChange={onChange}
          onDelete={onDelete}
          onToggle={onToggle}
        >
          {c.name}
        </ChecklistItem>
      );
    });
  return (
    <Checklist
      title={
        <StyledChecklistTitle>
          <span>Checklists</span>
          <StyledRequired>Required</StyledRequired>
        </StyledChecklistTitle>
      }
    >
      {renderChecklistItems}
      <ChecklistFooter disabled={isAdding} onAdd={onAdd} />
    </Checklist>
  );
};
